import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import { CustomTooltip } from '../styles';
import { formatCurrencyFlexible, nFormatter, formatString } from 'utils/helpers';
import { formatMaturityDate } from './formatMaturityDate';

const convertTerm = (term, section) => {
  term = String(term).normalize();
  if (term === 'Common Stock' && section === 'equityType') {
    return 'Non-Voting Common Stock';
  }
  if (term === 'Convertible Note' && section === 'offeringType') {
    return 'Convertible Notes';
  }

  return term;
};

// Fixed: Converted !== null checks to proper truthy checks to handle empty strings from API
export const TermsSections = (offeringData, dayjs, hideAmount, _amountRaisedObject) => {
  const simpleAgreementForFutureEquity = offeringData?.offeringType === 'safe';
  const isTTW = offeringData && offeringData.status === 'testing';

  return {
    amountRaised: !!offeringData?.amountRaised && (
      <Grid item container>
        {!hideAmount && (
          <>
            <Grid item container>
              <Typography variant="h8" color="common.divider">
                AMOUNT RAISED
              </Typography>
              <CustomTooltip
                content={`${offeringData?.name} is raising up to ${
                  offeringData?.maximumFundingAmount &&
                  formatCurrencyFlexible(Number.parseFloat(offeringData?.maximumFundingAmount))
                } with a minimum goal of ${
                  offeringData?.fundingGoal &&
                  formatCurrencyFlexible(Number.parseFloat(offeringData?.fundingGoal))
                }.
        The amount raised may include insider investments, which may go toward meeting the minimum offering amount.`}
              />
            </Grid>
            <Typography variant="h7" data-cy="campaign-amount-raised">
              {offeringData?.amountRaised &&
                formatCurrencyFlexible(Number.parseFloat(offeringData?.amountRaised))}
            </Typography>
          </>
        )}
      </Grid>
    ),
    pricePerShare: !!offeringData?.pricePerShare && (
      <Grid item>
        <Typography variant="h8" color="common.divider">
          PRICE PER SHARE
        </Typography>
        <Typography variant="h7" data-cy="campaign-price-per-share">
          {offeringData?.pricePerShare &&
            (() => {
              const amount = Number.parseFloat(offeringData?.pricePerShare);
              const minimumFractionDigits = Math.round(amount) === amount ? 0 : 2;

              try {
                const formatter = new Intl.NumberFormat(undefined, {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits,
                });
                return formatter.format(amount);
              } catch {
                // Intl.NumberFormat failed, using fallback
                // Fallback formatting
                if (!Number.isNaN(amount)) {
                  const decimals = minimumFractionDigits;
                  const formatted = amount.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                  return `$${formatted}`;
                }
                return '$0';
              }
            })()}
        </Typography>
      </Grid>
    ),
    discountRate: !!offeringData?.discountRate && (
      <Grid item container>
        <Grid item container>
          <Typography variant="h8" color="common.divider">
            DISCOUNT
          </Typography>
          <CustomTooltip content="The percent off the share price you pay when your note converts during the next equity raise." />
        </Grid>
        <Typography variant="h7" data-cy="campaign-discount-rate">
          {offeringData?.discountRate && `${parseFloat(offeringData?.discountRate).toFixed(1)}%`}
        </Typography>
      </Grid>
    ),
    interestRate: offeringData?.debtInterestRate !== null &&
      offeringData?.debtInterestRate !== undefined &&
      offeringData?.debtInterestRate !== '' && (
        <Grid item>
          <Typography variant="h8" color="common.divider">
            INTEREST RATE
          </Typography>
          <Typography variant="h7" data-cy="campaign-interest-date">
            {offeringData?.debtInterestRate !== null &&
            offeringData?.debtInterestRate !== undefined &&
            offeringData?.debtInterestRate !== ''
              ? `${parseFloat(offeringData?.debtInterestRate).toFixed(1)}%`
              : '0%'}
          </Typography>
        </Grid>
      ),
    deadline: !!offeringData?.endDate && (
      <Grid item container>
        <Grid item container>
          <Typography variant="h8" color="common.divider">
            DEADLINE
          </Typography>
          <CustomTooltip content="Closing date is based on the most recent SEC filing and may be subject to change. This campaign may be extended further if an amendment is filed." />
        </Grid>
        <Typography variant="h7" data-cy="campaign-deadline">
          {dayjs(offeringData?.endDate).tz().format('MMM[.] D[,] YYYY [at] h:mm A z')}
        </Typography>
      </Grid>
    ),
    maturityDate: !!offeringData?.debtMaturityDate && (
      <Grid item container>
        <Grid item container>
          <Typography variant="h8" color="common.divider">
            MATURITY DATE
          </Typography>
          <CustomTooltip
            content={`The date when the note is due to be repaid to the investor along with any accrued
      interest, if it has not yet converted to equity`}
          />
        </Grid>
        <Typography variant="h7" data-cy="campaign-maturity-date">
          {formatMaturityDate(dayjs, offeringData?.debtMaturityDate)}
        </Typography>
      </Grid>
    ),
    valuationCap: !!offeringData?.valuationCap &&
      offeringData?.valuationCap !== `0.00 USD` &&
      !isTTW && (
        <Grid item container>
          <Grid item container>
            <Typography variant="h8" color="common.divider">
              VALUATION CAP
            </Typography>
            <CustomTooltip
              content={`To give the noteholders a "ceiling" value at which their investment will
        convert and, in turn, that gives them a "floor" in regard to their ownership.`}
            />
          </Grid>
          <Typography variant="h7" data-cy="campaign-valuation-cap">
            {offeringData?.valuationCap &&
              `$${nFormatter(parseFloat(offeringData?.valuationCap), 2)}`}
          </Typography>
        </Grid>
      ),
    valuation: !!offeringData?.valuation && (
      <Grid item>
        <Typography variant="h8" color="common.divider">
          VALUATION
        </Typography>
        <Typography variant="h7" data-cy="campaign-valuation">
          {offeringData?.valuation && `$${nFormatter(parseFloat(offeringData?.valuation), 2)}`}
        </Typography>
      </Grid>
    ),
    fundingGoal: offeringData?.fundingGoal && offeringData?.maximumFundingAmount && (
      <Grid item container>
        <Grid item container>
          <Typography variant="h8" color="common.divider">
            FUNDING GOAL
          </Typography>
          <CustomTooltip
            content={`${offeringData?.name} is raising up to $${nFormatter(
              parseFloat(offeringData?.maximumFundingAmount),
              2,
            )} with a minimum goal of $${nFormatter(parseFloat(offeringData?.fundingGoal), 2)}.`}
          />
        </Grid>
        <Typography variant="h7" data-cy="campaign-funding-goal">
          {offeringData?.fundingGoal &&
            offeringData?.maximumFundingAmount &&
            `$${nFormatter(parseFloat(offeringData?.fundingGoal), 2)} - $${nFormatter(
              parseFloat(offeringData?.maximumFundingAmount),
              2,
            )}`}
        </Typography>
      </Grid>
    ),
    minInvestment: !!offeringData?.minimumInvestmentAmount && (
      <Grid item container>
        <Grid item container>
          <Typography variant="h8" color="common.divider">
            MIN INVESTMENT
          </Typography>
          <CustomTooltip
            content={`The smallest investment amount that ${offeringData?.name} is accepting.`}
          />
        </Grid>
        <Typography variant="h7" data-cy="campaign-min-investment">
          {offeringData?.minimumInvestmentAmount &&
            `${formatCurrencyFlexible(parseFloat(offeringData?.minimumInvestmentAmount))}`}
        </Typography>
      </Grid>
    ),
    maxInvestment: !!offeringData?.maximumInvestmentAmount && (
      <Grid item container>
        <Grid item container>
          <Typography variant="h8" color="common.divider">
            MAX INVESTMENT
          </Typography>
          <CustomTooltip
            content={`The biggest investment amount that ${offeringData?.name} is accepting.`}
          />
        </Grid>
        <Typography variant="h7" data-cy="campaign-max-investment">
          {offeringData?.maximumInvestmentAmount &&
            `${formatCurrencyFlexible(parseFloat(offeringData?.maximumInvestmentAmount))}`}
        </Typography>
      </Grid>
    ),
    offeringType: !!offeringData?.offeringType && (
      <Grid item>
        <Typography variant="h8" color="common.divider">
          OFFERING TYPE
        </Typography>
        <Typography variant="h7" data-cy="campaign-offering-type">
          {simpleAgreementForFutureEquity
            ? 'Simple Agreement for Future Equity'
            : offeringData?.offeringType &&
              convertTerm(
                offeringData?.offeringType
                  .split('_')
                  .map((word) => formatString.capitalizeFirstLetter(word))
                  .join(' '),
                'offeringType',
              )}
        </Typography>
      </Grid>
    ),
    minNumberShares: !!offeringData?.minimumNumberOfSharesOffered &&
      offeringData?.minimumNumberOfSharesOffered !== 0 && (
        <Grid item>
          <Typography variant="h8" color="common.divider">
            MIN NUMBER OF SHARES OFFERED
          </Typography>
          <Typography variant="h7" data-cy="campaign-min-number-shares-offered">
            {offeringData?.minimumNumberOfSharesOffered.toLocaleString()}
          </Typography>
        </Grid>
      ),
    maxNumberShares: !!offeringData?.maximumNumberOfSharesOffered &&
      offeringData?.maximumNumberOfSharesOffered !== 0 && (
        <Grid item>
          <Typography variant="h8" color="common.divider">
            MAX NUMBER OF SHARES OFFERED
          </Typography>
          <Typography variant="h7" data-cy="campaign-max-number-shares-offered">
            {offeringData?.maximumNumberOfSharesOffered.toLocaleString()}
          </Typography>
        </Grid>
      ),
    sharesOffered: !!offeringData?.securityName && (
      <Grid item>
        <Typography variant="h8" color="common.divider">
          SHARES OFFERED
        </Typography>
        <Typography variant="h7" data-cy="campaign-shares-offered">
          {offeringData?.securityName}
        </Typography>
      </Grid>
    ),
    conversionTrigger: !!offeringData?.conversion && (
      <Grid item>
        <Typography variant="h8" color="common.divider">
          CONVERSION TRIGGER
        </Typography>
        <Typography variant="h7" data-cy="campaign-conversion-trigger">
          {offeringData?.conversion
            ? formatCurrencyFlexible(parseFloat(offeringData?.conversion))
            : null}
        </Typography>
      </Grid>
    ),
    equityType: !!offeringData?.convertibleName && !simpleAgreementForFutureEquity && (
      <Grid item>
        <Typography variant="h8" color="common.divider">
          TYPE OF EQUITY
        </Typography>
        <Typography variant="h7" data-cy="campaign-equity-type">
          {offeringData?.convertibleName}
        </Typography>
      </Grid>
    ),
  };
};
